import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Claim } from '@wsphere/warranties/domain';
import { CLAIM_CLOSE_CONFIG } from '../../config/claim-close.config';
import { ClaimService } from '../../services';
import { escapeHtml } from '../../utils/escape-html.util';

/** Confirms closing a claim. A new-flow claim waits until every payment is paid; the API refuses too. */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-close-dialog',
  templateUrl: './claim-close.dialog.html',
  standalone: false,
})
export class ClaimCloseDialog {
  constructor(
    private claimSvc: ClaimService,
    private toastSvc: ToastService
  ) {}

  readonly config = CLAIM_CLOSE_CONFIG;

  @ViewChild(DialogComponent) dialog?: DialogComponent;

  claim: Claim.Model | null = null;

  /** Unpaid payments on a new-flow claim, read fresh each time the dialog opens. */
  unpaidPayments = 0;

  async open(claim: Claim.Model) {
    const fresh = Claim.Authorization.usesNewFlow(claim) ? await this.claimSvc.get(claim.id).catch(() => null) : null;
    this.claim = claim;
    this.unpaidPayments = fresh?.unpaidPayments ?? 0;
    this.dialog?.open();
  }

  close() {
    this.dialog?.close();
  }

  async confirm() {
    const claim = this.claim;
    if (!claim || this.unpaidPayments) return;

    const closedClaimId = await this.claimSvc.close(claim.id).catch(({ error }) => {
      const message = error?.message;
      const text = Array.isArray(message) ? message.join(' ') : String(message ?? '');
      this.toastSvc.show({ type: 'error', title: this.config.failed, text: escapeHtml(text) });
      this.close();
      return null;
    });
    if (!closedClaimId) return;

    this.toastSvc.show({ type: 'success', ...this.config.closed });
    this.close();
    await this.claimSvc.refreshClaim();
  }
}
